/**
 * DialogCoordinator: ensures only one dialog is active at a time and enables lazy loading on open.
 * Usage:
 *  - Import and call open('dialog-key') before showing a dialog.
 *  - Dialogs can subscribe with onOpen((name) => { if (name !== myKey) closeSelf(); })
 */

const CHANNEL = "dialog-open";

/**
 * Broadcast that a dialog with the given name is opening.
 * @param {string} name Unique key for the dialog (e.g., 'share', 'open', 'save', 'sheet', 'account', 'theme', 'options')
 */
export function open(name) {
  try {
    const event = new CustomEvent(CHANNEL, { detail: { name } });
    window.dispatchEvent(event);
  } catch (e) {
    // Fallback if CustomEvent is unavailable (very rare)
    console.warn("DialogCoordinator.open failed to dispatch event:", e);
  }
}

/**
 * Subscribe to dialog-open events.
 * Returns an unsubscribe function.
 * @param {(name: string) => void} handler
 * @returns {() => void}
 */
export function onOpen(handler) {
  if (typeof handler !== "function") return () => {};
  const listener = (e) => {
    try {
      const name = e?.detail?.name;
      if (name) handler(name);
    } catch (err) {
      console.warn("DialogCoordinator.onOpen handler error:", err);
    }
  };
  window.addEventListener(CHANNEL, listener);
  return () => window.removeEventListener(CHANNEL, listener);
}

export default { open, onOpen };
